// src/components/layout/SectionShell.tsx
import React from "react";
import clsx from "clsx";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeftIcon, XMarkIcon } from "@heroicons/react/24/outline";

import { A } from "../../styles/appleClasses";
import { companyRoute } from "../../utils/routeHelpers";
import SectionTabs, { type SectionTab } from "./SectionTabs";

export type SectionShellProps = {
  title: string;
  subtitle?: React.ReactNode;
  tabs?: SectionTab[];
  tabsAriaLabel?: string;
  closePath?: string;
  backPath?: string;
  backLabel?: string;
  closeLabel?: string;
  showBack?: boolean;
  showClose?: boolean;
  maxWidthClass?: string;
  contentMaxWidthClass?: string;
  className?: string;
  headerClassName?: string;
  contentClassName?: string;
  companyScopedTabs?: boolean;
  children: React.ReactNode;
};

function resolvePath(path: string, companyId: string | undefined) {
  if (!path.startsWith("/")) return path;
  return companyId ? companyRoute(companyId, path) : path;
}

export default function SectionShell({
  title,
  subtitle,
  tabs = [],
  tabsAriaLabel,
  closePath = "/",
  backPath,
  backLabel = "Back",
  closeLabel = "Close",
  showBack = true,
  showClose = true,
  maxWidthClass = "max-w-6xl",
  contentMaxWidthClass,
  className = "",
  headerClassName = "",
  contentClassName = "",
  companyScopedTabs = true,
  children,
}: SectionShellProps) {
  const navigate = useNavigate();
  const { company_id } = useParams<{ company_id?: string }>();

  const pageWrap = clsx("w-full mx-auto px-2 sm:px-6", maxWidthClass);
  const contentWrap = clsx(
    "w-full mx-auto px-2 sm:px-6 pt-4 pb-10",
    contentMaxWidthClass || maxWidthClass,
    contentClassName,
  );

  const iconBtn = clsx(
    A.iconBtn,
    "inline-flex items-center justify-center",
    "hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-appleblueD transition",
  );

  const onBack = () => {
    if (backPath) {
      navigate(resolvePath(backPath, company_id));
      return;
    }

    navigate(-1);
  };

  const onClose = () => {
    navigate(resolvePath(closePath, company_id));
  };

  return (
    <div className={clsx("min-h-screen bg-gray-50", A.body, className)}>
      <div
        className={clsx(
          "sticky top-0 z-30 bg-gray-50/90 backdrop-blur supports-[backdrop-filter]:bg-gray-50/70",
          headerClassName,
        )}
      >
        <div className={clsx(pageWrap, "pt-4 mb-2")}>
          <div className="flex items-center border-b border-gray-100 pb-2">
            {showBack ? (
              <button
                type="button"
                onClick={onBack}
                className={iconBtn}
                aria-label={backLabel}
              >
                <ArrowLeftIcon className="h-6 w-6 text-gray-400 hover:text-gray-600 transition" />
              </button>
            ) : (
              <div className={A.iconBtn} aria-hidden="true" />
            )}

            <div className="min-w-0 flex-1 text-center">
              <h1 className={clsx(A.title, "tracking-tight truncate")}>
                {title}
              </h1>

              {subtitle ? (
                <div
                  className={clsx(A.label, "text-gray-400 -mt-0.5 truncate")}
                >
                  {subtitle}
                </div>
              ) : null}
            </div>

            {showClose ? (
              <button
                type="button"
                onClick={onClose}
                className={iconBtn}
                aria-label={closeLabel}
              >
                <XMarkIcon className="h-6 w-6 text-gray-400 hover:text-gray-600 transition" />
              </button>
            ) : (
              <div className={A.iconBtn} aria-hidden="true" />
            )}
          </div>
        </div>
      </div>

      <div className={contentWrap}>
        {tabs.length ? (
          <div className="mb-3 flex items-center justify-between gap-3">
            <SectionTabs
              tabs={tabs}
              ariaLabel={tabsAriaLabel || `${title} sections`}
              companyScoped={companyScopedTabs}
            />
          </div>
        ) : null}

        {children}
      </div>
    </div>
  );
}

export type { SectionTab };